import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { useAuth, AUTH_ENABLED } from '../lib/auth';
import { OfferPlanEditor } from '../components/offers/OfferPlanEditor';
import { useOfferDetail } from '../hooks/useOffers';
import { useAnalyses } from '../hooks/useAnalyses';
import { useProfileDetail } from '../hooks/useProfiles';
import { calculateAll } from '../lib/calculator';
import { fmtDollarInt } from '../lib/format';
import type { UpdateData } from '../components/offers/OfferPlanEditor';
import type { OfferContingencies, OfferStatus, EscalationClause, Analysis, CalculatorInputs } from '../lib/types';


function OfferContent() {
  const { profileId, propertyId, offerId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getToken } = useAuth();

  const { profile } = useProfileDetail(profileId, getToken);
  const { offer, loading, error, updateOffer } = useOfferDetail(propertyId, offerId, getToken);
  const { analyses } = useAnalyses(propertyId, getToken);

  const latestAnalysis = analyses.length > 0 ? analyses[0] : null;
  const hasNewerAnalysis = !!(latestAnalysis && offer && latestAnalysis.id !== offer.analysisId);

  const [applyingUpdate, setApplyingUpdate] = useState(false);

  const handleApplyAnalysis = async (analysis: Analysis) => {
    if (!offer) return;
    setApplyingUpdate(true);
    try {
      const offerPrice = offer.offerPrice;
      const inputs: CalculatorInputs = {
        ...analysis.inputs,
        homePrice: offerPrice,
        downPaymentAmt: Math.round(offerPrice * (analysis.inputs.downPaymentPct / 100)),
      };
      const results = calculateAll(inputs);
      await updateOffer({ analysisId: analysis.id, inputs, results });
    } catch (err: any) {
      alert(`Failed to update: ${err.message}`);
    } finally {
      setApplyingUpdate(false);
    }
  };

  // Auto-apply from ?applyAnalysis param set by the AnalysisPage round-trip
  useEffect(() => {
    const applyId = searchParams.get('applyAnalysis');
    if (!applyId || !offer || analyses.length === 0) return;
    const toApply = analyses.find(a => a.id === applyId);
    if (toApply && toApply.id !== offer.analysisId) {
      handleApplyAnalysis(toApply);
    }
    // Clean up the param whether or not we applied (avoid re-triggering)
    setSearchParams(prev => { prev.delete('applyAnalysis'); return prev; }, { replace: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.get('applyAnalysis'), offer?.id, analyses.length]);

  const handleSave = async (data: UpdateData) => {
    // Pass updated inputs/results so the stored snapshot reflects any concession changes
    await updateOffer({
      earnestMoney: data.earnestMoney,
      closingDate: data.closingDate,
      contingencies: data.contingencies,
      notes: data.notes,
      status: data.status,
      escalation: data.escalation,
      inputs: data.inputs,
      results: data.results,
    });
    // No alert — OfferPlanEditor handles the ✓ Saved flash internally
  };

  if (loading) {
    return (
      <div className="page-container">
        <h2 className="page-title">Plan Offer</h2>
        <div className="empty-state"><div className="empty-state-text">Loading...</div></div>
      </div>
    );
  }

  if (error || !offer) {
    return (
      <div className="page-container">
        <h2 className="page-title">Plan Offer</h2>
        <div className="note-box" style={{ borderColor: 'var(--red)', background: 'rgba(239,68,68,0.1)' }}>
          {error || 'Offer not found'}
        </div>
        <Link
          to={`/profiles/${profileId}/properties/${propertyId}`}
          className="btn btn-secondary"
          style={{ marginTop: '1rem', display: 'inline-block' }}
        >
          ← Back to Property
        </Link>
      </div>
    );
  }

  const analysisPath = `/profiles/${profileId}/properties/${propertyId}/analyses/${offer.analysisId}`;

  return (
    <div className="page-container">
      <Link
        to={`/profiles/${profileId}/properties/${propertyId}`}
        style={{ color: 'var(--primary)', fontSize: '0.85rem', textDecoration: 'none' }}
      >
        ← Back to {profile?.name || 'Property'}
      </Link>
      <h2 className="page-title" style={{ marginTop: '0.75rem', marginBottom: '0.1rem' }}>
        Plan Offer — {fmtDollarInt(offer.offerPrice)}
      </h2>
      {offer.inputs.propertyAddress && (
        <p className="page-subtitle" style={{ marginTop: 0, marginBottom: '0.5rem' }}>
          {offer.inputs.propertyAddress}
          {offer.inputs.propertyCity ? `, ${offer.inputs.propertyCity}` : ''}
        </p>
      )}

      {/* Applying round-trip analysis update */}
      {applyingUpdate && (
        <div style={{
          padding: '0.6rem 0.9rem',
          background: 'rgba(59,130,246,0.08)',
          border: '1px solid rgba(59,130,246,0.3)',
          borderRadius: '8px',
          fontSize: '0.82rem',
          marginBottom: '1rem',
        }}>
          Updating analysis snapshot…
        </div>
      )}

      {/* Newer analysis available banner */}
      {hasNewerAnalysis && latestAnalysis && !applyingUpdate && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          padding: '0.6rem 0.9rem',
          background: 'rgba(59,130,246,0.08)',
          border: '1px solid rgba(59,130,246,0.3)',
          borderRadius: '8px',
          fontSize: '0.82rem',
          marginBottom: '1rem',
        }}>
          <span style={{ flex: 1 }}>
            A newer analysis is available (saved {new Date(latestAnalysis.createdAt).toLocaleDateString()}).
            Update this offer's snapshot?
          </span>
          <button
            className="btn btn-sm btn-primary"
            onClick={() => handleApplyAnalysis(latestAnalysis)}
            disabled={applyingUpdate}
          >
            Apply Update
          </button>
        </div>
      )}

      <OfferPlanEditor
        offer={offer}
        cashOnHand={profile?.cashOnHand ?? 0}
        analysisPath={analysisPath}
        onSave={handleSave}
        onCancel={() => window.history.back()}
      />
    </div>
  );
}

export function OfferPage() {
  if (!AUTH_ENABLED) {
    return (
      <div className="page-container">
        <h2 className="page-title">Plan Offer</h2>
        <div className="note-box">
          Authentication is required to view offers.
          Configure <code>VITE_CLERK_PUBLISHABLE_KEY</code> to enable authentication.
        </div>
      </div>
    );
  }

  return <OfferContent />;
}
